import {Button, IconButton} from '@wandb/components';
import React, {useCallback} from 'react';
import {useHistory} from 'react-router-dom';

import {useIsInLoadedReportContext} from '../../../pages/ReportPage/LoadedReportContext';
import {mediaCompareModeActivated} from '../../../services/analytics/productEvents';
import {usePanelContext} from '../../Panel/PanelContextProvider';
import {
  type MediaBrowserPanelProps,
  type MediaConfigWithDefaults,
} from '../Component';
import {useLayoutConfig} from '../layout/contexts/LayoutConfigContext';
import {
  getCompareModeEntryConfig,
  getCompareModeRunPair,
} from '../utils/compareEntryConfig';
import {useFullscreenPanelLink} from '../utils/useFullscreenPanelLink';

type CompareEntryControlProps = {
  config: MediaConfigWithDefaults;
  isFullScreenPanelPage?: boolean;
  updateConfig: MediaBrowserPanelProps['updateConfig'];
};

export const CompareEntryControl: React.FC<CompareEntryControlProps> = ({
  config,
  isFullScreenPanelPage = false,
  updateConfig,
}) => {
  const {trackPanelControls} = usePanelContext();
  const isInReport = useIsInLoadedReportContext();
  const history = useHistory();
  const fullscreenPanelLink = useFullscreenPanelLink();
  const layoutConfig = useLayoutConfig();
  const runs = layoutConfig?.getAvailableRuns() ?? [];
  const compareRunPair = getCompareModeRunPair(runs);
  const firstRunName = compareRunPair?.firstRunName;
  const secondRunName = compareRunPair?.secondRunName;
  const previousMode = config.mode;

  const handleClick = useCallback(() => {
    if (!firstRunName || !secondRunName) {
      return;
    }
    mediaCompareModeActivated({
      entryPoint: isFullScreenPanelPage
        ? 'fullscreen_panel'
        : 'workspace_panel',
      previousMode,
    });
    updateConfig(getCompareModeEntryConfig(firstRunName, secondRunName));
    if (!isFullScreenPanelPage) {
      window.scrollTo(0, 0);
      trackPanelControls({
        action: 'View full screen clicked',
      });
      history.push(fullscreenPanelLink);
    }
  }, [
    firstRunName,
    fullscreenPanelLink,
    history,
    isFullScreenPanelPage,
    previousMode,
    secondRunName,
    trackPanelControls,
    updateConfig,
  ]);

  if (!firstRunName || !secondRunName || isInReport) {
    return null;
  }

  return (
    <>
      <div className="hidden [@container(min-width:430px)]:block">
        <Button
          data-test="media-panel-compare-entry-button"
          size="md"
          variant="secondary"
          startIcon="miller-columns"
          onPress={handleClick}>
          Compare
        </Button>
      </div>
      <div className="block [@container(min-width:430px)]:hidden">
        <IconButton
          data-test="media-panel-compare-entry-icon-button"
          icon="miller-columns"
          size="md"
          tooltip={{
            children: 'Compare',
            placement: 'top',
          }}
          variant="secondary"
          onPress={handleClick}
          aria-label="Compare"
        />
      </div>
    </>
  );
};
